import { useEffect } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  useDisclosure
} from "@chakra-ui/react";
import { Form, Select } from "antd";
import { BiSlider } from "react-icons/bi";
import styled from "styled-components";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useFilters } from "core/features/filters/store";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import useFiltro from "../../../hooks/useFiltro";

type Props = {
  filtrosAvancados?: FiltrosAvancado;
}

const { Item, useForm } = Form;

const RelatorioPopoverContent = PopoverContent as any;
const RelatorioForm = Form as any;
const RelatorioPopoverHeader = PopoverHeader as any;
const RelatorioPopoverBody = PopoverBody as any;
const RelatorioPopoverFooter = PopoverFooter as any;
const RelatorioButton = Button as any;

export function FiltroAvancadoAderencia({ filtrosAvancados }: Props) {
  const [form] = useForm();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { filters, updateField } = useFilters();
  const setQueryParams = useRelatorioContext((s) => s.setQueryParams);
  const { filtersAdvanced } = filters;
  const { hasPermission } = useValidatePermission();
  const permissionProgramacaoOperacional = hasPermission(['Relatorios.CronogramaOperacional']);
  const permissionAnaliticoOperacional = hasPermission(['Relatorios.RelatorioAnaliticoOperacional']);
  const { filtroExecucaoOPeracional, update } = useFiltro();

  const activeFiltersCount = [
    !!filtersAdvanced?.operacao,
  ].filter(Boolean).length;

  function handleSearch() {
    const body = form.getFieldsValue();
    updateField("filtersAdvanced", {
      ...filtersAdvanced,
      classeOperacao: body?.classeOperacao ?? filtersAdvanced.classeOperacao,
      operacao: body?.operacao ?? filtersAdvanced.operacao
    });

    const has = filtrosAvancados?.operacoes?.filter(operacao =>
      form.getFieldValue('classeOperacao')?.includes(operacao.operacaoClasseId)
      && operacao.operacaoId === form.getFieldValue('operacao')
    );

    if (filtersAdvanced?.operacao && has?.length === 0) {
      form.setFieldsValue({ operacao: '' });
      updateField("filtersAdvanced", {
        ...filtersAdvanced,
        classeOperacao: body?.classeOperacao ?? filtersAdvanced.classeOperacao,
        operacao: ''
      });
    }
    onClose();
    queueMicrotask(() => {
      setQueryParams(body);
    });
  }

  function handleClear() {

    updateField("filtersAdvanced", {
      ...filtersAdvanced,
      operacao: '',
    });
    update({
      ...filtroExecucaoOPeracional,
      operacoes: [],
    });

    form.resetFields();
    onClose();
  }

  useEffect(() => {
    if (!filtersAdvanced?.operacao) {
      form.resetFields();
    }
  }, [!filtersAdvanced?.operacao]);


  return (
    <Popover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <ButtonSliderWrapper disabled={!permissionProgramacaoOperacional || !permissionAnaliticoOperacional}>
          {activeFiltersCount > 0 && <IconCircle>{activeFiltersCount}</IconCircle>}
          <BiSlider />
        </ButtonSliderWrapper>
      </PopoverTrigger>
      <RelatorioPopoverContent p={4} w="sm">
        <PopoverArrow />
        <PopoverCloseButton />
        <RelatorioPopoverHeader fontWeight="bold">Filtros Avançados</RelatorioPopoverHeader>
        <RelatorioForm form={form} layout="vertical">
          <RelatorioPopoverBody>
            <Wrap>
              <Item
                label="Classe de Operação"
                name="classeOperacao"
              >
                <SelectAntd
                  allowClear
                  showSearch
                  mode="multiple"
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues) => <span>+{omittedValues.length}</span>}
                  placeholder="Classe de Operação"
                  optionFilterProp="label"
                  defaultValue={filtersAdvanced?.classeOperacao}
                  options={(filtrosAvancados?.operacaoClasses || [])?.map((item: any) => ({
                    label: item.descricao,
                    value: item.operacaoClasseId,
                  }))}
                />
              </Item>
            </Wrap>
            <Wrap>
              <Item
                label="Operação"
                name="operacao"
              >
                <SelectAntd
                  allowClear
                  showSearch
                  placeholder="Operação"
                  defaultValue={filtersAdvanced?.operacao}
                  optionFilterProp="label"
                  options={(filtrosAvancados?.operacoes || [])
                    ?.filter(operacao => filtersAdvanced?.classeOperacao?.includes(operacao.operacaoClasseId))
                    ?.map((item: any) => ({
                      label: item.descricao,
                      value: item.operacaoId,
                    }))}
                />
              </Item>
            </Wrap>
          </RelatorioPopoverBody>

          <RelatorioPopoverFooter display="flex" justifyContent="space-between">
            <RelatorioButton variant="ghost" onClick={handleClear}>Limpar</RelatorioButton>
            <RelatorioButton colorScheme="green" onClick={handleSearch}>Aplicar</RelatorioButton>
          </RelatorioPopoverFooter>
        </RelatorioForm>
      </RelatorioPopoverContent>
    </Popover>
  );
}

const ButtonSliderWrapper = styled.button`
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    min-width: 45px;
    height: 60px;

    svg {
      font-size: 20px;
    }

    :active {
        background: #20a45a;
        border-color: #20a45a;
    }

    :disabled {
      opacity: 0.5;
      cursor: not-allowed;

    }
`;

const Wrap = styled.div`
    width: 100%;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-col {
      padding: 0;
    }
`;

const SelectAntd = styled(Select)`
  width: 100% !important;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }

  .ant-form-item-label {
    text-align: left !important;
  }

   &.ant-select-multiple > .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple > .ant-select-selector:hover {
    border: 1px solid #40a9ff !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple.ant-select-open > .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    border-right-width: 1px !important;
    outline: 0;
  }
`;

const IconCircle = styled.span`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 2px;
  right: 1px;
  border-radius: 50%;
  background-color: #ff0000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;`